import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ArrowRight, ArrowLeft, Loader2, CheckCircle2, Crosshair, Move } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { reverseGeocode } from "@/lib/geocode";
import MapPreview from "@/components/MapPreview";
import Seo from "@/components/Seo";

export default function OrderVerify() {
  const navigate = useNavigate();
  const [address, setAddress] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("mtr_order_address");
    if (!raw) { navigate("/dashboard", { replace: true }); return; }
    setAddress(JSON.parse(raw));
  }, [navigate]);

  const checkCoverage = async () => {
    setChecking(true);
    setConfirmed(true);
    try {
      const { data } = await api.post("/coverage", { address });
      setCoverage(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setConfirmed(false);
    } finally {
      setChecking(false);
    }
  };

  const proceed = (product) => {
    sessionStorage.setItem("mtr_order_product", JSON.stringify(product));
    navigate("/order/confirm");
  };

  const handlePinMove = async (newLat, newLng) => {
    // Optimistically move the pin; a manual drop means it's no longer a raw GPS fix.
    setAddress((prev) => {
      const next = { ...prev, lat: newLat, lng: newLng, source: "pin", accuracy: null };
      sessionStorage.setItem("mtr_order_address", JSON.stringify(next));
      return next;
    });
    try {
      const geo = await reverseGeocode(newLat, newLng);
      setAddress((prev) => {
        const next = {
          ...prev,
          lat: newLat,
          lng: newLng,
          full: geo.full || prev.full,
          street: geo.street,
          city: geo.city,
          state: geo.state,
          zip: geo.zip,
          source: "pin",
          accuracy: null,
        };
        sessionStorage.setItem("mtr_order_address", JSON.stringify(next));
        return next;
      });
    } catch {
      /* keep the dropped coordinates; leave the address text as-is */
    }
  };

  if (!address) return null;
  const available = coverage?.available && coverage.products?.length > 0;

  return (
    <div className="container-mtr py-8 sm:py-12 max-w-2xl" data-testid="order-verify-page">
      <Seo title="Confirm property — MeasureTheRoof" description="Confirm the property and check coverage." path="/order/verify" noindex />
      <button onClick={() => navigate("/dashboard")} className="text-mortar hover:text-iron flex items-center gap-1.5 text-[14px] mb-4" data-testid="verify-back">
        <ArrowLeft size={16} /> Back
      </button>

      <Steps current={1} />

      <div className="card-mtr mt-6">
        <span className="eyebrow">Step 1 · Confirm property</span>
        <h1 className="font-body font-extrabold text-navy text-[24px] mt-1 mb-1">Is this the right roof?</h1>
        <p className="text-mortar text-[14px] mb-4">Check the satellite view matches the structure you're bidding.</p>

        <MapPreview lat={address.lat} lng={address.lng} draggable={!confirmed} onPinMove={handlePinMove} unavailable={confirmed && coverage && !available} testid="verify-map" />
        {!confirmed && (
          <p className="text-[12px] text-mortar mt-2 flex items-center gap-1.5" data-testid="pin-drag-hint">
            <Move size={13} /> Not quite right? Drag the orange pin onto the exact roof — the address updates to match.
          </p>
        )}
        <div className="flex items-start gap-2 mt-3 p-3 rounded-input bg-fieldstone">
          <CheckCircle2 size={18} className="text-go-green shrink-0 mt-0.5" />
          <p className="text-[15px] text-iron font-semibold" data-testid="verify-address-text">{address.full}</p>
        </div>

        {address.source === "gps" && address.accuracy != null && (
          <GpsAccuracyHint meters={address.accuracy} onSearch={() => navigate("/dashboard")} />
        )}

        {!confirmed && (
          <button onClick={checkCoverage} className="btn-primary w-full mt-5" data-testid="confirm-address-btn">
            Confirm address & check coverage <ArrowRight size={18} />
          </button>
        )}
      </div>

      {confirmed && (
        <div className="card-mtr mt-5 animate-fade-up" data-testid="coverage-result">
          {checking ? (
            <div className="flex items-center gap-3 text-mortar py-4" data-testid="coverage-checking">
              <Loader2 className="animate-spin text-sky-blue" size={20} /> Checking imagery coverage…
            </div>
          ) : available ? (
            <>
              <div className="flex items-center gap-2 text-go-green mb-4">
                <ShieldCheck size={22} /> <span className="font-bold text-[16px]">Coverage available</span>
              </div>
              {coverage.products.map((p) => (
                <div key={p.productId} className="border-2 border-gravel rounded-input p-4" data-testid={`product-${p.productId}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-bold text-navy text-[18px]">{p.name}</h2>
                    <span className="metric text-[20px]">
                      ${Number(p.customerPriceMin).toFixed(2)}
                      {p.customerPriceMax && p.customerPriceMax !== p.customerPriceMin ? `–$${Number(p.customerPriceMax).toFixed(2)}` : ""}
                    </span>
                  </div>
                  <p className="text-[14px] text-mortar mt-2">{p.description}</p>
                  {p.isTemporarilyUnavailable && (
                    <p className="text-[13px] text-orange font-semibold mt-2">⚠ This product may be temporarily unavailable for this address.</p>
                  )}
                  <button onClick={() => proceed(p)} className="btn-primary w-full mt-4" data-testid={`select-product-${p.productId}`}>
                    Continue to order <ArrowRight size={18} />
                  </button>
                </div>
              ))}
              <p className="text-[12px] text-mortar mt-3">Final price is confirmed on the next step before any charge.</p>
            </>
          ) : (
            <div className="text-center py-4" data-testid="no-coverage">
              <div className="w-12 h-12 rounded-full bg-[#FDECEA] grid place-items-center mx-auto mb-3">
                <ShieldAlert className="text-caution-red" size={24} />
              </div>
              <h2 className="font-bold text-navy text-[18px]">No coverage available</h2>
              <p className="text-mortar text-[14px] mt-1 max-w-sm mx-auto">
                We don't have aerial imagery for this address right now, so a report can't be created. You haven't been charged.
              </p>
              <button onClick={() => navigate("/dashboard")} className="btn-ghost mt-5" data-testid="try-another-address">
                Try another address
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GpsAccuracyHint({ meters, onSearch }) {
  const good = meters <= 25;
  const fmt = meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${meters} m`;
  return (
    <div
      className={`mt-3 p-3 rounded-input flex items-start gap-2 ${good ? "bg-[#EAF7EF]" : "bg-[#FEF6EC]"}`}
      data-testid="gps-accuracy-hint"
    >
      <Crosshair size={18} className={`shrink-0 mt-0.5 ${good ? "text-go-green" : "text-orange"}`} />
      <div>
        <p className="text-[14px] font-semibold text-iron">
          Located you within ~{fmt}.{good ? " That's a precise fix — confirm below." : " Check the pin sits on the right roof before confirming."}
        </p>
        {!good && (
          <button onClick={onSearch} className="text-[13px] font-semibold text-orange hover:underline mt-1" data-testid="gps-search-instead">
            Not the right property? Search the address instead
          </button>
        )}
      </div>
    </div>
  );
}

export function Steps({ current }) {
  const steps = ["Confirm", "Pay", "Report"];
  return (
    <div className="flex items-center gap-2" aria-label={`Step ${current} of 3`}>
      {steps.map((s, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-[13px] font-bold shrink-0 ${active ? "bg-orange text-white" : done ? "bg-go-green text-white" : "bg-gravel text-mortar"}`}>
              {done ? "✓" : n}
            </div>
            <span className={`text-[13px] font-semibold ${active ? "text-navy" : "text-mortar"} hidden sm:block`}>{s}</span>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${done ? "bg-go-green" : "bg-gravel"}`} />}
          </div>
        );
      })}
    </div>
  );
}
